import { useCallback, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { CHATBOT_CONFIG } from '../../config';
import { chatBotService } from '../../services/ChatBotService';
import { ChatBotState } from '../../types';
import { saveChatSessionReadyState } from '../../utils/storage';
import { appendStreamText, createStreamBuffer } from '../../utils/streamUtils';
import { RootState } from '../../../../reducers';
import { idleTimer } from '../../../../actions/AuthActions/AuthenticationAction';

interface UseChatMessageFlowParams {
  labels: {
    errorFallbackMessage: string;
    unableToConnectBody: string;
  };
  getReduxState: () => RootState;
  setState: Dispatch<SetStateAction<ChatBotState>>;
  isSessionReadyRef: MutableRefObject<boolean>;
}

interface UseChatMessageFlowResult {
  sendMessage: (message: string, existingUserMessageId?: string) => Promise<boolean>;
  cancelOngoingRequest: () => void;
}

export const useChatMessageFlow = ({
  labels,
  getReduxState,
  setState,
  isSessionReadyRef,
}: UseChatMessageFlowParams): UseChatMessageFlowResult => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestInFlightRef = useRef(false);
  const isCancelRequestedRef = useRef(false);

  const sendMessage = useCallback(async (message: string, existingUserMessageId?: string): Promise<boolean> => {
    if (!message.trim() || requestInFlightRef.current) {
      return false;
    }

    idleTimer();

    requestInFlightRef.current = true;
    isCancelRequestedRef.current = false;

    let userMessageId = existingUserMessageId;

    if (existingUserMessageId) {
      setState(prev => ({
        ...prev,
        isLoading: true,
        isResponding: true,
        messages: prev.messages.map(msg =>
          msg.id === existingUserMessageId ? { ...msg, status: 'sending' as const } : msg
        ),
      }));
    } else {
      const userMessage = chatBotService.createMessage('user', message, 'sending');
      userMessageId = userMessage.id;
      setState(prev => ({
        ...prev,
        messages: [...prev.messages, userMessage],
        isLoading: true,
        isResponding: true,
      }));
    }

    if (!isSessionReadyRef.current) {
      const isSessionReady = await chatBotService.startSession();
      if (!isSessionReady) {
        const errorMessage = chatBotService.createMessage(
          'assistant',
          labels.unableToConnectBody,
          'error',
          {
            contentFormat: 'markdown',
            action: 'chat_connect_close',
          }
        );

        setState(prev => ({
          ...prev,
          messages: [...prev.messages, errorMessage],
          unreadCount: prev.isOpen ? prev.unreadCount : prev.unreadCount + 1,
          isLoading: false,
          isResponding: false,
        }));
        requestInFlightRef.current = false;
        return false;
      }

      isSessionReadyRef.current = true;
      saveChatSessionReadyState(true);
    }

    const assistantMessageId = `assistant-stream-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let hasAssistantContent = false;

    const appendAssistantChunk = (chunk: string) => {
      setState(prev => {
        const existingMessage = prev.messages.find(msg => msg.id === assistantMessageId);

        if (existingMessage) {
          return {
            ...prev,
            isLoading: false,
            messages: prev.messages.map(msg =>
              msg.id === assistantMessageId
                ? { ...msg, content: appendStreamText(msg.content, chunk), status: 'sent' as const }
                : msg
            ),
          };
        }

        const assistantMessage = {
          ...chatBotService.createMessage('assistant', chunk, 'sent', {
            contentFormat: 'markdown',
          }),
          id: assistantMessageId,
        };

        return {
          ...prev,
          messages: [...prev.messages, assistantMessage],
          unreadCount: prev.isOpen ? prev.unreadCount : prev.unreadCount + 1,
          isLoading: false,
        };
      });
    };

    const streamBuffer = createStreamBuffer((value) => {
      hasAssistantContent = true;
      appendAssistantChunk(value);
    });

    setState(prev => ({
      ...prev,
      messages: prev.messages.map(msg =>
        msg.id === userMessageId ? { ...msg, status: 'sent' as const } : msg
      ),
    }));

    try {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const response = await chatBotService.sendMessage(
        message,
        CHATBOT_CONFIG.enableContextCapture,
        CHATBOT_CONFIG.enableContextCapture ? getReduxState() : undefined,
        {
          signal: abortController.signal,
          onToken: (chunk) => {
            streamBuffer.push(chunk);
          },
          errorFallbackMessage: labels.errorFallbackMessage,
        }
      );
      streamBuffer.dispose();

      if (isCancelRequestedRef.current) {
        setState(prev => ({
          ...prev,
          isLoading: false,
        }));
        return false;
      }

      const errorRetryAction = response.status === 'error' ? 'retry_last_message' : undefined;
      const assistantContent = (response.status === 'error'
        ? (response.message || response.response || '')
        : (response.response || '')).trim();
      const shouldRenderSeparateAssistantMessage = Boolean(assistantContent)
        && !(response.status === 'error' && hasAssistantContent)
        && !(response.status === 'success' && hasAssistantContent);

      if (shouldRenderSeparateAssistantMessage) {
        const assistantMessage = chatBotService.createMessage(
          'assistant',
          assistantContent,
          response.status === 'success' ? 'sent' : 'error',
          {
            contentFormat: 'markdown',
            action: errorRetryAction,
          }
        );

        setState(prev => ({
          ...prev,
          messages: [
            ...prev.messages.map(msg =>
              msg.id === userMessageId && response.status === 'error'
                ? { ...msg, status: 'error' as const }
                : msg
            ),
            assistantMessage,
          ],
          isLoading: false,
          unreadCount: prev.isOpen ? prev.unreadCount : prev.unreadCount + 1,
        }));
      } else {
        setState(prev => ({
          ...prev,
          messages: prev.messages.map(msg =>
            msg.id === userMessageId && response.status === 'error'
              ? { ...msg, status: 'error' as const }
              : msg
          ),
          isLoading: false,
        }));
      }
      return response.status === 'success';
    } catch (error) {
      streamBuffer.dispose();
      console.error('Failed to send message:', error);

      const systemErrorMessage = chatBotService.createMessage(
        'assistant',
        labels.errorFallbackMessage,
        'error',
        {
          contentFormat: 'markdown',
          action: 'system_error_retry',
        }
      );

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, systemErrorMessage],
        unreadCount: prev.isOpen ? prev.unreadCount : prev.unreadCount + 1,
        isLoading: false,
      }));

      return false;
    } finally {
      setState(prev => ({ ...prev, isResponding: false }));
      abortControllerRef.current = null;
      requestInFlightRef.current = false;
      isCancelRequestedRef.current = false;
    }
  }, [isSessionReadyRef, labels.errorFallbackMessage, labels.unableToConnectBody, getReduxState, setState]);

  const cancelOngoingRequest = useCallback(() => {
    if (!requestInFlightRef.current) {
      return;
    }

    isCancelRequestedRef.current = true;
    abortControllerRef.current?.abort();
  }, []);

  return {
    sendMessage,
    cancelOngoingRequest,
  };
};
